import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Zap, BookOpen, Wrench, Home, FileText, ShieldCheck, Sparkles } from 'lucide-react';
interface ModuleCardProps {
  num: string;
  title: string;
  icon: React.ElementType;
  bullets: string[];
  spineColor: string;
  delay: number;
  isBonus?: boolean;
}
function ModuleCard({
  num,
  title,
  icon: Icon,
  bullets,
  spineColor,
  delay,
  isBonus
}: ModuleCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6,
    delay
  }} className={`relative rounded-3xl p-7 sm:p-8 flex flex-col overflow-hidden group transition-all ${isBonus ? 'bg-gradient-to-br from-ed-dark to-ed-darker border border-ed-yellow/40 hover:border-ed-yellow/70 shadow-[0_0_40px_rgba(255,214,10,0.1)] hover:shadow-[0_0_60px_rgba(255,214,10,0.25)]' : 'bg-ed-dark border border-zinc-800 hover:border-ed-green/40 hover:glow-green-sm'}`} data-id="element-398">
      {/* Glow */}
      <div className={`absolute -top-24 -right-24 w-64 h-64 blur-3xl rounded-full pointer-events-none ${isBonus ? 'bg-ed-yellow/10 group-hover:bg-ed-yellow/20' : 'bg-ed-green/5 group-hover:bg-ed-green/15'} transition-colors`} data-id="element-399"></div>

      {/* Top label */}
      <div className="relative z-10 flex items-center justify-between mb-5" data-id="element-400">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${isBonus ? 'bg-ed-yellow/15 border border-ed-yellow/40 text-ed-yellow' : 'bg-ed-green/10 border border-ed-green/30 text-ed-green'}`} data-id="element-401">
          {isBonus ? <Sparkles className="w-3 h-3" data-id="element-402" /> : <Zap className="w-3 h-3" data-id="element-403" />}
          {isBonus ? `Bonus ${num}` : `Módulo ${num}`}
        </div>
        <span className="text-5xl font-black opacity-15" style={{
        WebkitTextStroke: isBonus ? '1.5px rgba(255,214,10,0.4)' : '1.5px rgba(163,255,18,0.4)',
        color: 'transparent'
      }} data-id="element-404">
          {num}
        </span>
      </div>

      {/* Mockup */}
      <div className="relative z-10 w-28 h-36 mb-6 perspective-1000" data-id="element-405">
        <div className="w-full h-full relative preserve-3d transform rotate-[-10deg] group-hover:rotate-0 transition-transform duration-700" data-id="element-406">
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-ed-darker rounded-r-md rounded-l-sm border border-zinc-700/60 shadow-xl overflow-hidden" data-id="element-407">
            {num === '01' || num === '02' || num === '03' || isBonus && (num === '04' || num === '05') ? <>
                <img src={num === '01' ? 'https://cdn.magicpatterns.com/uploads/a1FEocdNHNBvyXakAamyW8/PORTADA_MODULO_1web.png' : num === '02' ? 'https://cdn.magicpatterns.com/uploads/ajR14EnHkjR7pf88KeU62q/1_PORTADA_MODULO_2_web.png' : num === '03' ? 'https://cdn.magicpatterns.com/uploads/q3pKPAZcWK2HzZkGKnSH6t/PORTADA_MODULO_3_web.png' : num === '04' ? 'https://cdn.magicpatterns.com/uploads/kNcbivAJdum6gmBrHY6YqW/PORTADA_MODULO_4_WEB.png' : 'https://cdn.magicpatterns.com/uploads/3jFjotxZ9iY3y3fC5pUKnn/RESUMEN_DE_NORMATIVA_WEB.png'} alt={`Manual Módulo ${num} — ${title}`} className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-408" />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-30 pointer-events-none" data-id="element-409"></div>
              </> : <>
                <div className={`absolute left-0 top-0 bottom-0 w-2 border-r border-black/20 z-10 ${spineColor}`} data-id="element-410"></div>
                <div className="absolute inset-0 pl-4 p-3 flex flex-col justify-between" data-id="element-411">
                  <div className="flex items-center justify-between" data-id="element-412">
                    <span className={`text-[9px] font-black uppercase tracking-widest ${isBonus ? 'text-ed-yellow' : 'text-ed-green'}`} data-id="element-413">
                      {isBonus ? 'Bonus' : `M${num}`}
                    </span>
                    <Zap className="w-3 h-3 text-zinc-600" data-id="element-414" />
                  </div>
                  <div className="flex-grow flex items-center justify-center" data-id="element-415">
                    <Icon className={`w-9 h-9 ${isBonus ? 'text-ed-yellow' : 'text-ed-green'}`} strokeWidth={1.5} data-id="element-416" />
                  </div>
                  <h4 className="text-white font-black text-[11px] uppercase leading-tight" data-id="element-417">
                    {title}
                  </h4>
                </div>
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-50 pointer-events-none" data-id="element-418"></div>
              </>}
          </div>
          <div className="absolute top-1 bottom-1 -right-1 w-1 bg-zinc-300 rounded-r-sm" data-id="element-419"></div>
        </div>
      </div>

      {/* Title */}
      <h3 className="relative z-10 text-2xl font-black text-white uppercase mb-5 leading-tight" data-id="element-420">
        {title}
      </h3>

      {/* Bullets */}
      <ul className="relative z-10 space-y-2.5 flex-grow" data-id="element-421">
        {bullets.map((b, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-422">
            <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${isBonus ? 'bg-ed-yellow' : 'bg-ed-green'}`} data-id="element-423"></div>
            <span className="text-sm text-zinc-300 font-medium leading-relaxed" data-id="element-424">
              {b}
            </span>
          </li>)}
      </ul>
    </motion.div>;
}
export function ProgramShowcase() {
  return <section id="showcase" className="py-24 bg-ed-darker relative border-t border-white/5 overflow-hidden" data-id="element-425">
      <div className="absolute inset-0 bg-radial-green opacity-25 pointer-events-none" data-id="element-426"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-427">
        <div className="text-center mb-14 max-w-2xl mx-auto" data-id="element-428">
          <span className="text-ed-green font-bold tracking-widest uppercase text-sm mb-3 block" data-id="element-429">
            El programa por dentro
          </span>
          <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-white mb-4" data-id="element-430">
            Todo lo que vas a <span className="text-ed-green" data-id="element-431">dominar.</span>
          </h2>
          <p className="text-zinc-400 text-lg" data-id="element-432">
            Tres módulos centrales + dos bonus exclusivos. Una formación
            integral pensada para transformarte en un electricista domiciliario
            profesional.
          </p>
        </div>

        {/* Main 3 modules */}
        <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-433">
          <ModuleCard num="01" title="Fundamentos" icon={BookOpen} spineColor="bg-ed-green" delay={0.1} bullets={['Conceptos eléctricos esenciales', 'Seguridad eléctrica en obra', 'Reglamentos y normativa básica', 'Componentes y materiales', 'Lectura de tableros y planos']} data-id="element-434" />
          <ModuleCard num="02" title="Montaje" icon={Wrench} spineColor="bg-ed-green" delay={0.2} bullets={['Instalaciones reales paso a paso', 'Cañerías y canalizaciones', 'Cableado profesional', 'Montaje práctico de tableros', 'Aplicación profesional en obra']} data-id="element-435" />
          <ModuleCard num="03" title="Instalación" icon={Home} spineColor="bg-ed-green" delay={0.3} bullets={['Instalaciones residenciales completas', 'Casos reales en viviendas', 'Diagnóstico y resolución de fallas', 'Criterio técnico profesional', 'Resolución de problemas reales']} data-id="element-436" />
        </div>

        {/* Bonuses divider */}
        <div className="relative my-12 flex items-center gap-4" data-id="element-437">
          <div className="flex-grow h-px bg-gradient-to-r from-transparent via-ed-yellow/30 to-transparent" data-id="element-438"></div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ed-yellow/10 border border-ed-yellow/40" data-id="element-439">
            <Sparkles className="w-4 h-4 text-ed-yellow" data-id="element-440" />
            <span className="text-xs font-black tracking-widest text-ed-yellow uppercase" data-id="element-441">
              Material extra incluido
            </span>
          </div>
          <div className="flex-grow h-px bg-gradient-to-r from-transparent via-ed-yellow/30 to-transparent" data-id="element-442"></div>
        </div>

        {/* 2 bonuses */}
        <div className="grid md:grid-cols-2 gap-6" data-id="element-443">
          <ModuleCard num="04" title="Guía de Circuitos Domiciliarios" icon={FileText} spineColor="bg-ed-yellow" delay={0.4} isBonus bullets={['Circuitos reales paso a paso', 'Comprensión visual de instalaciones', 'Herramienta práctica para obra', 'Casos aplicados a viviendas reales']} data-id="element-444" />
          <ModuleCard num="05" title="Resumen de Normativa Eléctrica" icon={ShieldCheck} spineColor="bg-ed-yellow" delay={0.5} isBonus bullets={['Normativa AEA simplificada', 'Aplicación práctica en instalaciones', 'Lo esencial sin tecnicismos', 'Referencia rápida para tu trabajo']} data-id="element-445" />
        </div>
      </div>
    </section>;
}